import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, Database, HardDrive } from 'lucide-react';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { summary } from '../../api/endpoints';
import type { DashboardSummary } from '../../api/endpoints';
import { formatBytes, formatCount } from '../../lib/format';
import { cx } from '../../lib/cx';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { HelpTip } from '../../components/ui/HelpTip';

/** Below this, an upload can start failing with INSUFFICIENT_STORAGE — say so loudly. */
export const DISK_CRITICAL_BYTES = 1024 * 1024 * 1024; // 1 GB
export const DISK_WARNING_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB

export type DiskPressure = 'ok' | 'low' | 'critical';

/** The DISK FREE tile's second line — one word, per the "Summary strip" section. */
export const DISK_STATE: Record<DiskPressure, string> = {
  ok: 'Healthy',
  low: 'Low',
  critical: 'Critical',
};

export function diskPressure(freeBytes: number): DiskPressure {
  if (freeBytes < DISK_CRITICAL_BYTES) return 'critical';
  if (freeBytes < DISK_WARNING_BYTES) return 'low';
  return 'ok';
}

/**
 * The help text for each tile, verbatim from the "Summary strip" section. A two-word label above a bare
 * number does not say *waiting for what* or *whose fault* — these do.
 */
export const TILE_HELP = {
  'Files waiting':
    'Files that have been uploaded but not yet downloaded, across every client. A file leaves storage the moment it is successfully downloaded, so this is everything still waiting to be picked up.',
  'Size waiting':
    "The total size of those waiting files. This is what counts against each client's storage quota — a client cannot upload more once its own share reaches the limit.",
  'Errors, last 24 h':
    'Failed attempts in the last 24 hours — uploads, downloads and listings alike. The second line splits them: caller means the request was refused as sent and they can fix it, ours means something failed on this side and may need a person here. Set the Status filter below to error to see them.',
  'Disk free':
    'Free space on the disk holding the file storage. If it runs out, uploads start failing with INSUFFICIENT_STORAGE for every client at once, and only a person can fix that — which is why it is on this screen.',
} as const;

export type TileLabel = keyof typeof TILE_HELP;

function Tile({
  label,
  value,
  detail,
  icon,
  tone = 'neutral',
}: {
  label: TileLabel;
  value: string;
  detail?: string;
  icon: ReactNode;
  tone?: 'neutral' | 'warning' | 'danger';
}) {
  // The tile, not the icon, is what the explanation describes — and what the bubble is
  // placed below, so it can never come down over the number it is explaining.
  const tileRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      ref={tileRef}
      className={cx(
        'relative flex items-start gap-2.5 rounded border py-2.5 pl-3 pr-7',
        tone === 'neutral' && 'border-hairline bg-surface',
        tone === 'warning' && 'border-warning bg-warning-soft',
        tone === 'danger' && 'border-danger bg-danger-soft',
      )}
    >
      <span
        className={cx(
          'mt-0.5',
          tone === 'neutral' && 'text-ink-subtle',
          tone === 'warning' && 'text-warning',
          tone === 'danger' && 'text-danger',
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-2xs uppercase tracking-wide text-ink-muted">{label}</p>
        {/*
          The strip answers "is everything all right?" without reading a table row, so the
          four numbers are the largest type on the screen rather than the same 15px as
          everything around them. `.tnum` and not `.tabular`: these are read, not compared
          down a column, and the monospaced face was putting a full mono space between the
          figure and its unit ("918  KB").
        */}
        <p className="tnum text-[22px] font-semibold leading-7 text-ink">{value}</p>
        {detail ? <p className="text-2xs text-ink-muted">{detail}</p> : null}
      </div>
      <HelpTip
        className="absolute right-1.5 top-1.5"
        anchor={tileRef}
        label={`What does ${label.toUpperCase()} show?`}
        text={TILE_HELP[label]}
      />
    </div>
  );
}

export function SummaryStrip() {
  const query = useQuery<DashboardSummary>({
    queryKey: ['summary'],
    queryFn: () => summary.get(),
    refetchInterval: 60_000,
  });

  if (query.isLoading) {
    return (
      <div className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-[72px] animate-pulse rounded border border-hairline bg-surface"
          />
        ))}
      </div>
    );
  }

  if (query.isError)
    return <ErrorNotice error={query.error} fallback="The summary could not be loaded." />;

  const data = query.data;
  if (!data) return null;

  const pressure = diskPressure(data.diskFreeBytes);

  return (
    <div className="flex shrink-0 flex-col gap-3">
      {pressure !== 'ok' ? (
        <div
          role="status"
          className={cx(
            'flex items-start gap-2 rounded border px-3 py-2 text-[13px]',
            pressure === 'critical'
              ? 'border-danger bg-danger-soft'
              : 'border-warning bg-warning-soft',
          )}
        >
          <AlertTriangle
            size={15}
            className={cx(
              'mt-0.5 shrink-0',
              pressure === 'critical' ? 'text-danger' : 'text-warning',
            )}
            aria-hidden="true"
          />
          <p className="text-ink">
            Only <span className="tabular font-semibold">{formatBytes(data.diskFreeBytes)}</span> of
            disk space is left.{' '}
            {pressure === 'critical'
              ? 'Uploads are about to start failing with INSUFFICIENT_STORAGE — free space now.'
              : 'Free space soon, or uploads will start failing with INSUFFICIENT_STORAGE.'}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Files waiting"
          value={formatCount(data.filesWaiting)}
          detail={`${formatCount(data.activeClients)} of ${formatCount(data.clients)} clients active`}
          icon={<Clock size={16} />}
        />
        <Tile
          label="Size waiting"
          value={formatBytes(data.bytesWaiting)}
          detail="Uploaded, not yet picked up"
          icon={<Database size={16} />}
        />
        <Tile
          label="Errors, last 24 h"
          value={formatCount(data.errorsLast24h)}
          // The breakdown of the number above it, not unrelated traffic. It previously
          // read "3 uploads · 2 downloads" — successful ones — under a headline counting
          // errors, so the two lines could not be reconciled and the reader was left
          // doing arithmetic that never worked.
          detail={`${formatCount(data.clientFaultsLast24h)} caller · ${formatCount(
            data.serverFaultsLast24h,
          )} ours`}
          icon={<AlertTriangle size={16} />}
          tone={data.errorsLast24h > 0 ? 'warning' : 'neutral'}
        />
        <Tile
          label="Disk free"
          value={formatBytes(data.diskFreeBytes)}
          // See the "Summary strip" section: "A one-word state: Healthy, Low, or Critical." The banner above
          // the strip is where INSUFFICIENT_STORAGE is named outright, not here.
          detail={DISK_STATE[pressure]}
          icon={<HardDrive size={16} />}
          tone={pressure === 'critical' ? 'danger' : pressure === 'low' ? 'warning' : 'neutral'}
        />
      </div>
    </div>
  );
}
